import { Loader } from "@sicredi/react";
import React, { lazy, Suspense } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";

import { PAGE } from "#/constants";
import { OidcProvider, withOidcSecure } from "@axa-fr/react-oidc";
import { QueryClientProvider } from "react-query";
import { configParceriasGovBR } from "./api/oidc/oidcConfig";
import AuthenticatingPage from "./components/Auth/Authenticating/Authenticating";
import AuthenticatingErrorPage from "./components/Auth/AuthenticatingError/AuthenticatingError";
import CallbackSuccessPage from "./components/Auth/CallbackSuccess/CallbackSuccess";
import LoadingPage from "./components/Auth/Loading/Loading";
import SessionLostPage from "./components/Auth/SessionLost/SessionLost";
import FooterContainer from "./components/Footer/FooterContainer";
import HeaderContainer from "./components/Header/HeaderContainer";
import { queryClient } from "./query-client-config";
import { CategoryPartnerProvider } from "./context/useCategoryPartner";
import { CategoryPartnerLocalProvider } from "./context/useCategoryPartnerLocal";

const LazyHome = lazy(() => import("#/containers/Home"));
const LazyFAQ = lazy(() => import("#/containers/FAQ"));
const LazyCart = lazy(() => import("#/containers/Cart"));
const LazyPayment = lazy(() => import("#/containers/Payment"));
const LazyBenefitsPartner = lazy(() => import("#/containers/BenefitsPartner"));
const LazyError = lazy(() => import("#/containers/Error"));
const LazySuccess = lazy(() => import("#/containers/Success"));
const LazyCategories = lazy(() => import("#/containers/Category"));
const LazyPartnerDetail = lazy(() => import("#/containers/PartnerDetail"));
const LazyNotFound = lazy(() => import("#/containers/NotFound"));
const LazyPartnersShowcase = lazy(
  () => import("#/containers/PartnersShowcase")
);
const LazyRVRefillPhone = lazy(() => import("#/containers/RV/RVRefillPhone"));
const LazyRVRefillPhoneValues = lazy(
  () => import("#/containers/RV/RVRefillPhoneValues")
);

const LazyRVTVPlans = lazy(() => import("#/containers/RV/TvPlans"));

const LazyRVTVPlansConfirm = lazy(
  () => import("#/containers/RV/TvPlansConfirm")
);

const LazyRVPin = lazy(() => import("#/containers/RV/Pin"));
const LazyRVPinConfirm = lazy(() => import("#/containers/RV/PinConfirm"));
const LazyRecharges = lazy(() => import("#/containers/Recharges"));
const LazyMyOrders = lazy(() => import("#/containers/MyOrders"));
const LazyMyOrdersDetails = lazy(() => import("#/containers/OrderDetails"));

// TODO fazer as telas abaixo
export const Loading = () => <LoadingPage />;
export const AuthenticatingError = () => <AuthenticatingErrorPage />;
export const Authenticating = () => <AuthenticatingPage />;
export const SessionLost = () => <SessionLostPage />;
export const ServiceWorkerNotSupported = () => <ServiceWorkerNotSupported />;
export const CallBackSuccess = () => <CallbackSuccessPage />;

export const Routes: React.FC = () => (
  <OidcProvider
    configuration={window.env?.OIDC_AUTH ?? configParceriasGovBR}
    loadingComponent={Loading}
    authenticatingErrorComponent={AuthenticatingError}
    authenticatingComponent={Authenticating}
    sessionLostComponent={SessionLost}
    //onSessionLost={onSessionLost} // If set "sessionLostComponent" is not displayed and onSessionLost callback is called instead
    serviceWorkerNotSupportedComponent={ServiceWorkerNotSupported}
    callbackSuccessComponent={CallBackSuccess}
    // getFetch={() => fetchWithLogs(fetch)}
  >
    <Router basename={process.env.PUBLIC_URL}>
      <Suspense fallback={<Loader show fullScreen />}>
        <QueryClientProvider client={queryClient}>
          <CategoryPartnerProvider>
            <CategoryPartnerLocalProvider>
              <HeaderContainer />
              <Switch>
                <Route
                  exact
                  path={PAGE.ROOT()}
                  component={withOidcSecure(LazyHome)}
                />
                <Route
                  exact
                  path={PAGE.FAQ()}
                  component={withOidcSecure(LazyFAQ)}
                />
                <Route
                  exact
                  path={PAGE.CART()}
                  component={withOidcSecure(LazyCart)}
                />
                <Route
                  exact
                  path={PAGE.PAYMENT(":pixid")}
                  component={withOidcSecure(LazyPayment)}
                />
                <Route
                  exact
                  path={PAGE.CATEGORIES()}
                  component={withOidcSecure(LazyCategories)}
                />
                <Route
                  exact
                  path={PAGE.PARTNER()}
                  component={withOidcSecure(LazyPartnerDetail)}
                />
                <Route
                  exact
                  path={PAGE.ERROR(":local")}
                  component={withOidcSecure(LazyError)}
                />
                <Route
                  exact
                  path={PAGE.SUCCESS(":local")}
                  component={withOidcSecure(LazySuccess)}
                />
                <Route
                  exact
                  path={PAGE.BENEFITS_PARTNER()}
                  component={withOidcSecure(LazyBenefitsPartner)}
                />
                <Route
                  exact
                  path={PAGE.PARTNERS_SHOWCASE()}
                  component={withOidcSecure(LazyPartnersShowcase)}
                />
                <Route
                  exact
                  path={PAGE.RV_REFILL_PHONE()}
                  component={withOidcSecure(LazyRVRefillPhone)}
                />
                <Route
                  exact
                  path={PAGE.RV_REFILL_PHONE_VALUES()}
                  component={withOidcSecure(LazyRVRefillPhoneValues)}
                />
                <Route
                  exact
                  path={PAGE.RV_REFILL_TV(":provider")}
                  component={withOidcSecure(LazyRVTVPlans)}
                />
                <Route
                  exact
                  path={PAGE.RV_REFILL_TV_CONFIRM(":provider", ":productId")}
                  component={withOidcSecure(LazyRVTVPlansConfirm)}
                />
                <Route
                  exact
                  path={PAGE.RV_REFILL_PIN(":provider")}
                  component={withOidcSecure(LazyRVPin)}
                />
                <Route
                  exact
                  path={PAGE.RV_REFILL_PIN_CONFIRM(":provider", ":productId")}
                  component={withOidcSecure(LazyRVPinConfirm)}
                />
                <Route
                  exact
                  path={PAGE.RECHARGES()}
                  component={withOidcSecure(LazyRecharges)}
                />
                <Route
                  exact
                  path={PAGE.MY_ORDERS()}
                  component={withOidcSecure(LazyMyOrders)}
                />
                <Route
                  exact
                  path={PAGE.MY_ORDERS_DETAILS(":orderNumber")}
                  component={withOidcSecure(LazyMyOrdersDetails)}
                />
                <Route path="*" component={LazyNotFound} />
              </Switch>
              <FooterContainer />
            </CategoryPartnerLocalProvider>
          </CategoryPartnerProvider>
        </QueryClientProvider>
      </Suspense>
    </Router>
  </OidcProvider>
);
